import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { Field, Formik } from 'formik';
import { AppNotificationAlert } from '@/layout/AppComponents';
import * as Yup from 'yup';
import PasswordStrengthBar from 'react-password-strength-bar';
import axios from 'axios';
import ReactDOMServer from 'react-dom/server';
import Translator from '../../layout/components/helpers/Translator';
import BreadcrumbHandler from '../../layout/components/breadcrumb/BreadcrumbHandler';
import { axiosPath, path } from '../../routing/helpers/router';
import * as authActions from '../../store/actions/authAction';

const ChangePasswordScreen = () => {
  const navigate = useNavigate();
  const changePassword = useSelector(
    (state) => state?.auth?.permissions?.client?.global?.changePassword,
  );
  const dispatch = useDispatch();
  const [errors, setErrors] = useState();

  // < LOAD CHANGE PASSWORD
  const handleSubmit = async (values) => {
    await axios
      .post(axiosPath('api_r_user_authUser_changePassword'), values)
      .then(async (response) => {
        await dispatch(authActions.fetchAuthUser());
        navigate(path({ name: 'user_home' }));
        if (response?.data?.message) {
          AppNotificationAlert({
            title: response.data.title,
            message: response.data.message,
            type: 'success',
            duration: 3000,
          });
        }
      })
      .catch((err) => {
        if (err?.response?.data?.message) {
          AppNotificationAlert({
            title: 'Error',
            message: err.response.data.message,
            type: 'danger',
            duration: 3000,
          });
        }
        if (err?.response?.data?.errors) setErrors(err?.response?.data?.errors);
      });
  };
  // > LOAD CHANGE PASSWORD

  // < FORM SCHEMA
  const validationSchema = Yup.object().shape({
    newPass: Yup.string().required(
      ReactDOMServer.renderToString(
        <Translator fiisle="user" name="user.required" />,
      ),
    ),
    newPassRepeat: Yup.string()
      .required(
        ReactDOMServer.renderToString(
          <Translator file="user" name="user.required" />,
        ),
      )
      .test(
        'passwords-match',
        ReactDOMServer.renderToString(
          <Translator file="user" name="user.password.change.bad-repeat" />,
        ),
        function (value) {
          return this.parent.newPass === value;
        },
      ),
  });
  // < FORM SCHEMA

  return (
    <section className="relative m_sr p-3">
      <div className="content-header">
        <BreadcrumbHandler
          paths={[
            {
              name: ReactDOMServer.renderToString(
                <Translator file="user" name="user.password.change" />,
              ),
            },
          ]}
        />
      </div>
      <section className="content">
        <div className="row">
          <div className="col-sm-12 col-md-6 col-lg-4 offset-md-3 offset-lg-4">
            <div className="card">
              <div className="card-header">
                <Translator file="user" name="user.password.change" />
              </div>
              <div className="card-body">
                {changePassword?.requireStrength && (
                  <div className="row">
                    {changePassword?.minCharacters && (
                      <div className="col-lg-12">
                        <p>
                          *{' '}
                          <Translator
                            file="user"
                            name="user.password.minCharacters"
                            variables={{
                              minCharacters: changePassword?.minCharacters,
                            }}
                            isHtml
                          />
                        </p>
                      </div>
                    )}
                    {(changePassword?.minAlphabetic ||
                      changePassword?.minNumeric ||
                      changePassword?.minSpecial) && (
                      <div className="col-lg-12">
                        <p>
                          *{' '}
                          <Translator
                            file="user"
                            name="user.password.compulsory.include"
                          />
                          :
                        </p>
                        <ul>
                          {changePassword?.minAlphabetic && (
                            <p className={errors?.minAlphabetic ? 'text-danger' : ''}>
                              *{' '}
                              <Translator
                                file="user"
                                name="user.password.minAlphabetic"
                                variables={{
                                  minAlphabetic: changePassword?.minAlphabetic,
                                }}
                                isHtml
                              />
                            </p>
                          )}
                          {changePassword?.minNumeric && (
                            <p className={errors?.minNumeric ? 'text-danger' : ''}>
                              *{' '}
                              <Translator
                                file="user"
                                name="user.password.minNumeric"
                                variables={{
                                  minNumeric: changePassword?.minNumeric,
                                }}
                                isHtml
                              />
                            </p>
                          )}
                          {changePassword?.minSpecial && (
                            <p className={errors?.minSpecial ? 'text-danger' : ''}>
                              *{' '}
                              <Translator
                                file="user"
                                name="user.password.minSpecial"
                                variables={{
                                  minSpecial: changePassword?.minSpecial,
                                }}
                                isHtml
                              />
                            </p>
                          )}
                        </ul>
                        {changePassword?.specialCharacters && (
                          <p
                            className={errors?.specialCharacters ? 'text-danger' : ''}
                          >
                            *{' '}
                            <Translator
                              file="user"
                              name="user.password.specialCharacters"
                              variables={{
                                specialCharacters: changePassword?.specialCharacters,
                              }}
                              isHtml
                            />
                          </p>
                        )}
                        {changePassword?.forbiddenUsername && (
                          <p
                            className={errors?.forbiddenUsername ? 'text-danger' : ''}
                          >
                            *{' '}
                            <Translator
                              file="user"
                              name="user.password.forbiddenUsername"
                              variables={{
                                forbiddenUsername: changePassword?.forbiddenUsername,
                              }}
                              isHtml
                            />
                          </p>
                        )}
                        {changePassword?.forbiddenWords && (
                          <p className={errors?.forbiddenWords ? 'text-danger' : ''}>
                            *{' '}
                            <Translator
                              file="user"
                              name="user.password.forbiddenWords"
                            />{' '}
                            {changePassword?.forbiddenWords
                              .split(',')
                              .map((fw, i) => (
                                <b key={i}>
                                  {i > 0 && ', '}
                                  {fw}
                                </b>
                              ))}
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                )}
                <Formik
                  initialValues={{
                    oldPass: '',
                    newPass: '',
                    newPassRepeat: '',
                  }}
                  validationSchema={validationSchema}
                  onSubmit={(values) => handleSubmit(values)}
                >
                  {({ handleSubmit, values, errors, touched, isSubmitting }) => (
                    <>
                      <div className="wl-title wl-title-md wl-title-color font-weight-bold mb-1">
                        <Translator file="user" name="user.password.old" />
                      </div>
                      <Field
                        name="oldPass"
                        type="password"
                        className={`form-control mb-2 ${
                          touched.oldPass && errors?.oldPass ? 'error' : ''
                        }`}
                      />
                      {touched.oldPass && errors.oldPass && (
                        <div className="text-danger">*{errors.oldPass}</div>
                      )}
                      <hr />
                      <div className="wl-title wl-title-md wl-title-color font-weight-bold mb-1">
                        <Translator file="user" name="user.password.new" />
                      </div>
                      <Field
                        name="newPass"
                        type="password"
                        className={`form-control mb-2 ${
                          touched.newPass && errors?.newPass ? 'error' : ''
                        }`}
                      />
                      {touched.newPass && errors.newPass && (
                        <div className="text-danger">*{errors.newPass}</div>
                      )}
                      <PasswordStrengthBar
                        password={values.newPass}
                        minLength={6}
                        scoreWords={[
                          ReactDOMServer.renderToString(
                            <Translator file="user" name="password.too.weak" />,
                          ),
                          ReactDOMServer.renderToString(
                            <Translator file="user" name="password.weak" />,
                          ),
                          ReactDOMServer.renderToString(
                            <Translator file="user" name="password.regular" />,
                          ),
                          ReactDOMServer.renderToString(
                            <Translator file="user" name="password.safe" />,
                          ),
                          ReactDOMServer.renderToString(
                            <Translator file="user" name="password.very.safe" />,
                          ),
                        ]}
                        shortScoreWord={[
                          ReactDOMServer.renderToString(
                            <Translator file="user" name="password.too.short" />,
                          ),
                        ]}
                      />
                      <div className="wl-title wl-title-md wl-title-color font-weight-bold mb-1">
                        <Translator file="user" name="user.password.repeat" />
                      </div>
                      <Field
                        name="newPassRepeat"
                        type="password"
                        className={`form-control mb-2 ${
                          touched.newPassRepeat && errors?.newPassRepeat
                            ? 'error'
                            : ''
                        }`}
                      />
                      {touched.newPassRepeat && errors.newPassRepeat && (
                        <div className="text-danger">*{errors.newPassRepeat}</div>
                      )}
                      <span
                        className={`btn btn-sm btn-primary float-right mt-2${
                          isSubmitting ? ' disabled' : ''
                        }`}
                        onClick={() => (isSubmitting ? undefined : handleSubmit())}
                      >
                        <Translator file="user" name="user.confirm" />
                      </span>
                    </>
                  )}
                </Formik>
              </div>
            </div>
          </div>
        </div>
      </section>
    </section>
  );
};

export default ChangePasswordScreen;
